import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import _ from 'lodash';
import CheckoutBreadCrumb from './CheckoutBreadCrumb';
import ReduxSuspense from '../../components/ReduxSuspense/ReduxSuspense';
import { AddressHeader } from './CheckoutComponents/AddressDetailComponents/AddressHeader';
import RadioGroup from '../../components/form-elements/RadioGroup';
import Image from '../../components/Image/Image';
import SelectSublocation from './CheckoutComponents/Reservation/SelectSublocation';
import { sessionIdSelector } from '../../../modules/selectors/auth';
import { standortUrlSelector } from '../../../modules/selectors/url';
import { apiClient, useAsyncFunction } from '../../../helpers/apiHelperV2';

import Input from '../../components/form-elements/Input';
import VehicleIdentNumInfoDialog from '../../components/VehicleIdentNumInfoDialog/VehicleIdentNumInfoDialog';
import { selectedSublocationSelector } from '../../../modules/selectors/standort/subLocation';
import Button from '../../components/form-elements/Button';
import Mileage from '../../components/form-fragments/Mileage';
import { useStandortHistory } from '../../components/VHost/StandortHistory';
import StandortRedirect from '../../components/VHost/StandortRedirect';

const WITH_ASSEMBLY = 'withAssembly';
const WITHOUT_ASSEMBLY = 'withoutAssembly';

export const ReservationDetailSchema = Yup.object().shape({
    selectedSublocation: Yup.string().required('Pflichtfeld'), // Location ID
    selectedCarBrand: Yup.string().required('Pflichtfeld'),
    requestedAssembly: Yup.string().when(['hasAssemblyProduct', 'hasServiceProduct'], {
        is: (hasAssemblyProduct, hasServiceProduct) => hasAssemblyProduct && !hasServiceProduct,
        then: Yup.string().oneOf([WITH_ASSEMBLY, WITHOUT_ASSEMBLY], 'Pflichtfeld').required('Pflichtfeld'),
    }),
    vehicleIdentificationNumber: Yup.string().length(17, 'Die Fahrgestellnummer muss 17-Stellig sein'),
    licensePlate: Yup.string(),
    mileage: Yup.number(),
    desiredSchedule: Yup.string(),
    notes: Yup.string(),
    // Context Fields
    hasAssemblyProduct: Yup.boolean(),
    hasServiceProduct: Yup.boolean(),
});

function ReservationDetailsForm({ checkout }) {
    const history = useStandortHistory();
    const sessionId = useSelector(sessionIdSelector);
    const standortUrl = useSelector(standortUrlSelector);
    const globalSelectedSublocation = useSelector(selectedSublocationSelector);

    const { initialValues, steps } = useMemo(() => {
        const { appointment, checkoutSteps: steps = [], selectedSublocation, selectedCarBrand, cart } = checkout;

        const hasAssemblyProduct = cart.products.some(p => p.selectedLieferweg.type === 'abholung-montage');
        const hasServiceProduct = cart.products.some(p => p.selectedLieferweg.type === 'werkstatt');
        const requestedAssembly = appointment?.requestedAssembly;
        const productsHerstellersList = cart.products.map(p => p.herstellerangaben);

    
        const initialValues = {
            selectedSublocation: selectedSublocation?._id ?? globalSelectedSublocation?._id ?? '',
            selectedCarBrand: selectedCarBrand || '',
            requestedAssembly: _.isBoolean(requestedAssembly) ? (requestedAssembly ? WITH_ASSEMBLY : WITHOUT_ASSEMBLY) : '',
            vehicleIdentificationNumber: appointment?.fahrgestellnummer || '',
            licensePlate: appointment?.kennzeichen || '',
            mileage: appointment?.kmStatus || '',
            desiredSchedule: appointment?.desiredSchedule || '',
            notes: appointment?.hinweise || '',
            hasAssemblyProduct,
            hasServiceProduct,
            productsHerstellersList,
        };

        return {
            initialValues,
            steps,
        };
    }, [checkout, globalSelectedSublocation?._id]);

    return (
        <div className="checkout">
            <CheckoutBreadCrumb partials={steps} standortUrl={standortUrl} title="Reservierung" />
            <Formik
                initialValues={initialValues}
                validationSchema={ReservationDetailSchema}
                onSubmit={async (values, actions) => {
                    try {
                        const checkoutData = {
                            appointment: {
                                ...checkout?.appointment,
                                requestedAssembly: values.requestedAssembly === WITH_ASSEMBLY,
                                fahrgestellnummer: values.vehicleIdentificationNumber,
                                kennzeichen: values.licensePlate,
                                kmStatus: values.mileage,
                                desiredSchedule: values.desiredSchedule,
                                hinweise: values.notes,
                            },
                            selectedSublocation: values.selectedSublocation,
                            selectedCarBrand: values.selectedCarBrand,
                        };
                        const partial = steps.find(s => s.active);

                        await apiClient.post('/api/v1/PersistCheckout', { checkoutData, sessionId });

                        const {
                            data: { data },
                        } = await apiClient.post(`/api/v1/getNextCheckoutUrl/${partial.key}`, {
                            sessionId,
                        });

                        return history.push(data.redirectUrl);
                    } catch (err) {
                        console.error(err);
                        window.UIkit.notification('Es ist ein Fehler aufgetreten, bitte versuchen Sie es später erneut.', {
                            status: 'error',
                            pos: 'top-right',
                        });
                    } finally {
                        actions.setSubmitting(false);
                    }
                }}
            >
                {({ values, errors, touched, setFieldValue, handleBlur }) => {
                    const isAssembly = values.requestedAssembly === WITH_ASSEMBLY;
                    const { hasAssemblyProduct, hasServiceProduct } = values;
                    return (
                        <Form className="uk-form-stacked">
                            <SelectSublocation
                                values={values}
                                setFieldValue={setFieldValue}
                                handleBlur={handleBlur}
                                touched={touched}
                                errors={errors}
                            />
                            {hasAssemblyProduct && !hasServiceProduct && (
                                <AddressHeader icon="fa-wrench" title="Möchten Sie eine Montage?" hideButton>
                                    <RadioGroup
                                        name="requestedAssembly"
                                        value={values.requestedAssembly}
                                        onChange={({ name, value }) => setFieldValue(name, value)}
                                        onBlur={handleBlur}
                                        options={[
                                            {
                                                label: (
                                                    <>
                                                        <Image
                                                            src="https://techno-images.s3.eu-central-1.amazonaws.com/images/Icons/Icon-Qualitaetsgarantie.png"
                                                            alt="Qualitätsgarantie"
                                                            resizeWidth={80}
                                                            resizeHeight={40}
                                                            fit="contain"
                                                            className="uk-padding-small uk-padding-remove-vertical"
                                                            background="transparent"
                                                            enlarge
                                                        />
                                                        Unverbindliches Montage-Angebot gewünscht
                                                    </>
                                                ),
                                                value: WITH_ASSEMBLY,
                                            },
                                            {
                                                label: (
                                                    <>
                                                        <Image
                                                            src="https://techno-images.s3.eu-central-1.amazonaws.com/icons/APS_Icon_Selbstabholung.png"
                                                            alt="Selbstabholung"
                                                            resizeWidth={80}
                                                            resizeHeight={40}
                                                            fit="contain"
                                                            className="uk-padding-small uk-padding-remove-vertical"
                                                            background="transparent"
                                                            enlarge
                                                        />
                                                        Ware reservieren <b>OHNE</b> zusätzliches Montage-Angebot
                                                    </>
                                                ),
                                                value: WITHOUT_ASSEMBLY,
                                            },
                                        ]}
                                        error={touched.requestedAssembly && errors.requestedAssembly ? errors.requestedAssembly : undefined}
                                    />
                                </AddressHeader>
                            )}
                            {(isAssembly || hasServiceProduct) && (
                                <>
                                    <AddressHeader icon="fa-car" title="Fahrzeugdaten" hideButton>
                                        <p>
                                            Um die benötigten Ersatzteile/Services schnell und eindeutig für Ihr Auto identifizieren zu
                                            können, wird die Fahrzeugidentifikationsnummer benötigt. Diese wird ausschließlich zu
                                            Auftragsbearbeitung genutzt.
                                        </p>
                                        <Input
                                            name="vehicleIdentificationNumber"
                                            value={values.vehicleIdentificationNumber}
                                            label={
                                                <>
                                                    Fahrgestellnummer (17-stellig) <VehicleIdentNumInfoDialog />
                                                </>
                                            }
                                            placeholder="Feld E in den Autopapieren"
                                            onChange={({ name, value }) => setFieldValue(name, value.toUpperCase())}
                                            onBlur={handleBlur}
                                            error={
                                                touched.vehicleIdentificationNumber && errors.vehicleIdentificationNumber
                                                    ? errors.vehicleIdentificationNumber
                                                    : undefined
                                            }
                                        />
                                        <Input
                                            name="licensePlate"
                                            value={values.licensePlate}
                                            label="Kennzeichen"
                                            placeholder="AB-CD-123"
                                            onChange={({ name, value }) => setFieldValue(name, value.toUpperCase())}
                                            onBlur={handleBlur}
                                            error={touched.licensePlate && errors.licensePlate ? errors.licensePlate : undefined}
                                            // Reference: https://de.wikipedia.org/wiki/Euro-Kennzeichen#Standard-Kennzeichen_der_EU-Staaten
                                            maxLength={12}
                                        />
                                        <Mileage
                                            errors={errors}
                                            touched={touched}
                                            values={values}
                                            onChange={({ name, value }) => setFieldValue(name, value)}
                                            onBlur={handleBlur}
                                        />
                                    </AddressHeader>
                                    <AddressHeader icon="fa-info" title="Weitere Hinweise" hideButton>
                                        <Input
                                            name="desiredSchedule"
                                            value={values.desiredSchedule}
                                            label="Gewünschter Werkstatttermin"
                                            placeholder="Bsp.: Dienstags oder Donnerstag nachmittags"
                                            onChange={({ name, value }) => setFieldValue(name, value)}
                                            onBlur={handleBlur}
                                            error={touched.desiredSchedule && errors.desiredSchedule ? errors.desiredSchedule : undefined}
                                        />
                                        <Input
                                            name="notes"
                                            value={values.notes}
                                            label="Weitere Hinweise"
                                            placeholder="Freitext für Ihre Fragen"
                                            onChange={({ name, value }) => setFieldValue(name, value)}
                                            onBlur={handleBlur}
                                            error={touched.notes && errors.notes ? errors.notes : undefined}
                                        />
                                    </AddressHeader>
                                </>
                            )}
                            <Button fullWidth type="submit">
                                WEITER ZUR RESERVIERUNG
                            </Button>
                        </Form>
                    );
                }}
            </Formik>
            <div className="required-text uk-text uk-margin-small-top">
                <p>* Pflichtfeld</p>
            </div>
        </div>
    );
}

function ReservationDetails() {
    const sessionId = useSelector(sessionIdSelector);
    const standortUrl = useSelector(standortUrlSelector);

    const initialValuesResponse = useAsyncFunction(async () => {
        const {
            data: { data = {} },
        } = await apiClient.post('/api/v1/checkout/standortAuswahl', {
            sessionId,
            standortUrl: standortUrl,
        });

        if (data.redirect && typeof data.redirect !== 'boolean') return { redirectUrl: data.redirect };
        if (data.redirectUrl) return { redirectUrl: data.redirectUrl };
        const checkout = data.checkoutData || {};

        return { checkout };
    }, [sessionId, standortUrl]);

    return (
        <div className="checkout_wrapper">
            <ReduxSuspense response={initialValuesResponse}>
                {({ redirectUrl, checkout }) => {
                    if (redirectUrl) return <StandortRedirect to={redirectUrl} push />;
                    return <ReservationDetailsForm checkout={checkout} />;
                }}
            </ReduxSuspense>
        </div>
    );
}

export default ReservationDetails;
